import { useRef, type ReactNode } from "react";
import { DollarSignIcon } from "lucide-react";

import { Row } from "@/components";
import {
  HexagonIconBadge,
  MetricLeadersHoverCard,
  RevenueRacetrack,
  RevenueThroneScene,
  RevenueThroneSummary,
} from "@/components/leaderboard";
import { Card, CardContent } from "@/components/ui/card";
import {
  useInactiveUserIds,
  useTaggableUsers,
} from "@/contexts/TaggableUsersContext";
import { useGetLeaderboard } from "@/hooks/queries/useGetLeaderboard";
import { useGetPets } from "@/hooks/queries/useGetPets";
import { useGetTeamGoal } from "@/hooks/queries/useGetTeamGoal";
import { REVENUE_GRADIENT } from "@/lib/constants";
import { formatting } from "@/lib/formatting";
import { leaderboardMetrics } from "@/lib/leaderboardMetrics";
import {
  leaderboardGoal,
  type RacetrackGoalScope,
} from "@/lib/leaderboardGoal";
import { stafferPets } from "@/lib/stafferPets";
import { teamGoals } from "@/lib/teamGoals";
import { cn } from "@/lib/utils";

import {
  TeamGoalField,
  UserRole,
  type LeaderboardQueryParams,
  type PetAppearance,
  type TeamGoal,
} from "@/types";

const RACETRACK_MIN_CARD_HEIGHT_PX = 409;
// Same height on every screen; short screens cap it to one viewport less the
// 24px page padding above and below.
const RACETRACK_CARD_HEIGHT_PX = 800;
// Lanes fill the card below this line; the throne scene keeps its stairs above.
const FLOOR_TOP_RATIO = 0.4;

// Per-staffer revenue goal for the finish line: an even share of the month's
// team goal, scaled to a week when the selected period is weekly.
const perStafferRevenueGoal = (
  teamGoal: TeamGoal | null | undefined,
  scope: RacetrackGoalScope | null,
): number | undefined => {
  if (!teamGoal || teamGoal.numStaffers <= 0 || scope === null)
    return undefined;

  const monthly = teamGoals.perStafferGoal(
    teamGoal.revenueGoal,
    teamGoal.numStaffers,
    TeamGoalField.REVENUE,
  );
  if (scope === "WEEK") {
    return teamGoals.weeklyGoal(monthly, TeamGoalField.REVENUE);
  }
  return monthly;
};

export const MostRevenueAndRacetrackContainer = ({
  queryParams,
  isNaturalHeight = false,
  controls,
}: {
  queryParams: LeaderboardQueryParams;
  // Lanes at full size and the card grows to fit them (the trophy case sizes
  // itself off this). Default is a fixed-height card with lanes fit below the
  // throne.
  isNaturalHeight?: boolean;
  // Pinned to the card's top-right corner.
  controls?: ReactNode;
}) => {
  /* React Hooks */
  const { users: taggableUsers } = useTaggableUsers();
  const inactiveUserIds = useInactiveUserIds();
  const { data: pets } = useGetPets({ select: stafferPets.toAppearances });
  const resolution = leaderboardGoal.resolvePeriod(queryParams);

  // The race always reflects the selected period; ranges wider than a month
  // just drop the goal (see leaderboardGoal) and race to the top staffer.
  const { data: raceData, isPlaceholderData } = useGetLeaderboard(queryParams);

  // Wait for staffer's  data to refresh before refreshing goal
  const committedResolution = useRef(resolution);
  if (!isPlaceholderData) committedResolution.current = resolution;
  const activeResolution = committedResolution.current;

  const { data: teamGoal } = useGetTeamGoal(
    activeResolution.goalMonth,
    activeResolution.goalYear,
  );

  /* Helpers */
  const activeStafferIds = new Set(
    taggableUsers
      .filter((u) => u.deletedAt === null && u.userRole === UserRole.STAFFER)
      .map((u) => u.id),
  );
  // Deactivated staffers still race if they earned revenue this period
  // (e.g. a placement closed before they were deleted).
  const racers = raceData
    ? raceData.filter((u) => activeStafferIds.has(u.userId) || u.revenue > 0)
    : [];

  // Each lane's racer — missing entry when the staffer hasn't adopted a pet.
  const petByUserId = new Map<number, PetAppearance>(
    (pets ?? []).map((pet) => [pet.userId, pet]),
  );

  const revenueGoalPerStaffer = perStafferRevenueGoal(
    teamGoal,
    activeResolution.goalScope,
  );

  const maxRevenue = Math.max(...racers.map((u) => u.revenue), 0);
  const leaders = racers
    .filter((u) => u.revenue === maxRevenue && maxRevenue > 0)
    .sort((a, b) => a.firstName.localeCompare(b.firstName));
  // First top earner in API order sits the throne.
  const throneLeader = racers.find(
    (u) => u.revenue === maxRevenue && maxRevenue > 0,
  );

  /* Render */
  const renderRevenueHoverBadge = (backdropColor?: string) => (
    <MetricLeadersHoverCard
      title="Most Revenue"
      icon={DollarSignIcon}
      gradient={REVENUE_GRADIENT}
      leaders={leaderboardMetrics.topN(racers, "revenue", 3)}
      valueOf={(u) => u.revenue}
      format={formatting.currency}
      badgeBackdropColor={backdropColor}
    >
      <Row className="cursor-default">
        <HexagonIconBadge
          icon={DollarSignIcon}
          gradient={REVENUE_GRADIENT}
          backdropColor={backdropColor}
        />
      </Row>
    </MetricLeadersHoverCard>
  );

  const renderRacetrack = () => (
    <RevenueRacetrack
      users={racers}
      revenueGoalPerStaffer={revenueGoalPerStaffer}
      goalScope={activeResolution.goalScope}
      isWinter
      isFitToHeight={!isNaturalHeight}
      petByUserId={petByUserId}
      inactiveUserIds={inactiveUserIds}
    />
  );

  const render = () => {
    return (
      <Card
        className="relative flex flex-col overflow-hidden rounded-md border-0 bg-slate-950 shadow-sm"
        style={
          isNaturalHeight
            ? { minHeight: RACETRACK_MIN_CARD_HEIGHT_PX }
            : {
                height: `min(${RACETRACK_CARD_HEIGHT_PX.toString()}px, calc(100svh - 3rem))`,
              }
        }
      >
        <RevenueThroneScene
          leader={throneLeader}
          petByUserId={petByUserId}
          floorTopRatio={isNaturalHeight ? undefined : FLOOR_TOP_RATIO}
        />
        {controls}
        <CardContent className="relative z-[1] flex min-h-0 flex-1 flex-col p-0">
          <Row className="items-start gap-3 px-7 pr-60 pt-6">
            <RevenueThroneSummary leaders={leaders} maxRevenue={maxRevenue} />
            {renderRevenueHoverBadge("#cbd5e1")}
          </Row>
          <Row
            className={cn(
              "mt-auto px-12 pb-4 pt-8",
              // Keep the lanes on the floor below the throne and its staircase.
              !isNaturalHeight && "min-h-0 shrink-0 px-8 pt-6",
            )}
            style={
              isNaturalHeight
                ? undefined
                : { height: `${((1 - FLOOR_TOP_RATIO) * 100).toString()}%` }
            }
          >
            {renderRacetrack()}
          </Row>
        </CardContent>
      </Card>
    );
  };

  return render();
};
